/**
 * `/agents/new` — create-agent form (RLY-024 AC #3).
 *
 * Phase-1 backend choice is restricted to `anthropic_messages` (the only
 * backend the orchestrator's `defaultExecutorForBackend` accepts at run-
 * dispatch time today — see `apps/orchestrator/src/functions/run-agent.ts:405`).
 *
 * Standing order [12]: validation uses `CreateAgentRequestSchema` from
 * `@simpliwork/api-schema` — the same schema the API parses against, so a
 * client-side success guarantees the API will accept the payload.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import type { CreateAgentRequest } from "@simpliwork/api-schema";
import { CreateAgentRequestSchema, modelsByProvider } from "@simpliwork/api-schema";
import { useMcpInstances } from "@/hooks/use-mcp";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";
import { useCreateAgent } from "@/hooks/use-agents";
import { ApiError } from "@/lib/api-client";

const DEFAULT_VALUES: CreateAgentRequest = {
  name: "",
  description: "",
  config: {
    backend: "anthropic_messages",
    // M1.2 default model — Sonnet 4.6 sits at the 1× reference burn rate.
    // Legacy stored values (e.g. claude-sonnet-4-5) auto-upgrade at dispatch
    // via @simpliwork/api-schema/models LEGACY_MODEL_ALIASES.
    model: "claude-sonnet-4-6",
    system_prompt: "You are a helpful assistant.",
    // M1.7 (BYOK teardown) — LLM provider keys are operator secrets at the
    // Worker level; tenants no longer supply them. secrets_refs stays as
    // an empty default so non-LLM secrets (Twilio, GitHub, etc.) can still
    // be added per-agent.
    secrets_refs: [],
    mcp_bindings: [],
    // RLY-036 — Phase-1 default for the egress allow-list. `["*"]` is the
    // wildcard form (no Phase-1 enforcement); Phase-5 narrows this per the
    // runbook at `docs/runbooks/egress-enforcement-rollout.md`.
    allowed_egress: ["*"],
  },
  limits: {
    timeout_seconds: 60,
    max_tokens: 4_096,
    max_cost_micros: 1_000_000, // $1.00
  },
};

export function AgentNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const createAgent = useCreateAgent();
  const mcpInstances = useMcpInstances();
  const noConnections =
    !mcpInstances.isLoading && (mcpInstances.data?.items.length ?? 0) === 0;

  const form = useForm<CreateAgentRequest>({
    // The double-installed-zod cast pattern documented in App.tsx (RLY-013) —
    // miniflare pins zod@3.22.3 transitively while the workspace pins 3.24.1.
    resolver: zodResolver(CreateAgentRequestSchema) as never,
    defaultValues: DEFAULT_VALUES,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const result = await createAgent.mutateAsync(values);
      toast.toast({
        title: "Agent created",
        description: `${values.name} (v${result.version})`,
        variant: "success",
      });
      navigate(`/agents/${result.agent_id}`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({
        title: "Could not create agent",
        description: message,
        variant: "error",
      });
    }
  });

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Create agent</h1>
        <p className="text-sm text-muted-foreground">Configure a new agent in this workspace.</p>
      </header>

      {/* Connections-first nudge — informational, doesn't block. Agents
          dispatch fine without MCP bindings, but most templates only do
          something useful once a tool is connected. */}
      {noConnections && (
        <div
          data-testid="agent-new-no-connections-nudge"
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p className="font-semibold">No tools connected yet</p>
          <p className="mt-1 text-amber-800">
            Agents work without connections, but most templates expect a tool to act on
            (Twilio for SMS, Gmail for inbound, Meta/LinkedIn for posts). Consider{" "}
            <Link to="/connections" className="font-medium underline">
              wiring up a connection first
            </Link>
            .
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Agent configuration</CardTitle>
          <CardDescription>
            Phase-1 only ships the <code>anthropic_messages</code> backend; more soon.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={onSubmit}
            data-testid="agent-new-form"
            noValidate
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-name">Name</Label>
              <Input
                id="agent-name"
                data-testid="agent-name-input"
                placeholder="My research assistant"
                {...form.register("name")}
              />
              {form.formState.errors.name && (
                <p data-testid="agent-name-error" className="text-xs text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-description">Description (optional)</Label>
              <Input
                id="agent-description"
                data-testid="agent-description-input"
                placeholder="What this agent does"
                {...form.register("description")}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-backend">Backend</Label>
              <select
                id="agent-backend"
                data-testid="agent-backend-input"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                {...form.register("config.backend")}
              >
                <option value="anthropic_messages">anthropic_messages (Phase 1)</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-model">Model</Label>
              <select
                id="agent-model"
                data-testid="agent-model-input"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                {...form.register("config.model")}
              >
                {Array.from(modelsByProvider().entries()).map(([provider, entries]) => (
                  <optgroup key={provider} label={provider === "anthropic" ? "Anthropic" : "OpenAI"}>
                    {entries.map((m) => (
                      <option key={m.modelId} value={m.modelId}>
                        {m.displayName} — {m.multiplier}× burn rate
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Burn rate is the credit multiplier per provider token. Sonnet 4.6 / GPT-5.4 are the
                1× reference models. Higher = burns more credits per token; lower = burns fewer.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agent-system-prompt">System prompt</Label>
              <Textarea
                id="agent-system-prompt"
                data-testid="agent-system-prompt-input"
                rows={6}
                {...form.register("config.system_prompt")}
              />
              {(() => {
                const errs = form.formState.errors;
                const configErrors = errs.config as
                  | Record<string, { message?: string } | undefined>
                  | undefined;
                const promptError = configErrors?.system_prompt;
                return promptError?.message ? (
                  <p data-testid="agent-system-prompt-error" className="text-xs text-destructive">
                    {promptError.message}
                  </p>
                ) : null;
              })()}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="agent-max-tokens">max_tokens</Label>
                <Input
                  id="agent-max-tokens"
                  type="number"
                  min={1}
                  data-testid="agent-max-tokens-input"
                  {...form.register("limits.max_tokens", { valueAsNumber: true })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="agent-timeout">timeout_seconds</Label>
                <Input
                  id="agent-timeout"
                  type="number"
                  min={1}
                  data-testid="agent-timeout-input"
                  {...form.register("limits.timeout_seconds", { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Link to="/agents">
                <Button type="button" variant="outline" data-testid="agent-new-cancel">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" data-testid="agent-new-submit" disabled={createAgent.isPending}>
                {createAgent.isPending ? "Creating…" : "Create agent"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default AgentNewPage;
